import { type DocumentCompileRequest, useDocumentsWorker } from '@Contexts/documents';
import { type BlobInput, type PageSize } from '@oicana/browser';
import { useEffect, useRef, useState } from 'react';

export interface UseCompiledTemplateResult {
    documentToken?: number;
    pages: PageSize[];
}

/**
 * Compiles the given template whenever its inputs change and keeps the resulting document
 * token alive until it is superseded by a newer compile or this hook unmounts.
 */
// @ai-generated 2026-07-21T09:40:08+00:00 by matthias.gessinger@synqony.com using Claude Opus 4.8
export function useCompiledTemplate(
    template: string,
    jsonDatasets: Map<string, string>,
    blobDatasets: Map<string, BlobInput>,
    onError: (error: unknown) => void,
): UseCompiledTemplateResult {
    const [documentToken, setDocumentToken] = useState<number>();
    const [pages, setPages] = useState<PageSize[]>([]);
    const { compile, release } = useDocumentsWorker();

    const documentTokenRef = useRef<number>(undefined);
    const runningCompileRef = useRef<Promise<void>>(undefined);

    useEffect(() => {
        // Tracks whether THIS effect run's own compile is still the one that matters: flipped to
        // false by the cleanup below, which React calls both when a newer compile supersedes this
        // one and on unmount. A stale/late-arriving result never touches state or onError; if it
        // did resolve to a real document, that document is released immediately instead of leaking.
        let isCurrent = true;

        const compileRequest: DocumentCompileRequest = { template, jsonDatasets, blobDatasets };

        // Serialize compiles for this template so a burst of prop changes doesn't flood the worker:
        // each compile waits for the previous one to settle before starting.
        const previousCompile = runningCompileRef.current ?? Promise.resolve();
        const compilation = previousCompile.then(() => compile(compileRequest));
        // Hand the next effect run something to await. The two-arg then swallows this compile's
        // rejection so one failure doesn't break the next compile's await (the chain below still
        // reports it via onError). We intentionally never clear this ref: a newer compile may have
        // already chained onto it, and clearing would let the one after that skip the queue and race.
        runningCompileRef.current = compilation.then(
            () => {},
            () => {},
        );

        compilation
            .then((compileResult) => {
                if (compileResult === undefined) {
                    return;
                }
                if (!isCurrent) {
                    release(compileResult.documentToken);
                    return;
                }
                const previousToken = documentTokenRef.current;
                if (previousToken !== undefined) {
                    release(previousToken);
                }
                documentTokenRef.current = compileResult.documentToken;
                setDocumentToken(compileResult.documentToken);
                setPages(compileResult.pages);
            })
            .catch((error) => {
                if (isCurrent) {
                    onError(error);
                }
            });

        return () => {
            isCurrent = false;
        };
    }, [template, jsonDatasets, blobDatasets, compile, release, onError]);

    useEffect(() => {
        return () => {
            if (documentTokenRef.current !== undefined) {
                release(documentTokenRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { documentToken, pages };
}
